import { createApp } from "vue";
import { createPinia } from "pinia";
import piniaPluginPersistedState from "pinia-plugin-persistedstate";
import App from "./App.vue";
import router from "./router";
import "./assets/css/app.css";
import { getApp, getApps, initializeApp } from "firebase/app";
import { connectFirestoreEmulator, getFirestore } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import {
  AppCheck,
  getToken,
  initializeAppCheck,
  ReCaptchaEnterpriseProvider
} from "firebase/app-check";
import VueVirtualScroller from "vue-virtual-scroller";
import "vue-virtual-scroller/dist/vue-virtual-scroller.css";
import { getMessaging, onMessage } from "firebase/messaging";
import * as Sentry from "@sentry/vue";
import {
  esProduccion,
  redirigirAccesoAtiendo
} from "./constants/accesoAtiendo";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

const firebaseApp =
  getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();

const auth = getAuth(firebaseApp);
const db = getFirestore(firebaseApp);
export const messaging = getMessaging(firebaseApp);

if (import.meta.env.VITE_USAR_EMULADOR == "true") {
  console.warn("*** Conectado al emulador de Firestore ***");
  connectFirestoreEmulator(db, "127.0.0.1", 8081);
}

const appCheckKey = import.meta.env.VITE_FIREBASE_APPCHECK_KEY;

if (import.meta.env.DEV) {
  const token = localStorage.getItem("tokenDepuracion");
  if (token) {
    (self as any).FIREBASE_APPCHECK_DEBUG_TOKEN = token;
  } else {
    (self as any).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
  }
}

const appCheck: AppCheck = initializeAppCheck(firebaseApp, {
  provider: new ReCaptchaEnterpriseProvider(appCheckKey),
  isTokenAutoRefreshEnabled: true
});

const app = createApp(App);

Sentry.init({
  app,
  dsn: "https://ccce5212b4b7ccc1cf53e2f3b853c726@o4511139760046080.ingest.us.sentry.io/4511139763519488",
  environment: import.meta.env.VITE_ENV_NOMBRE,
  integrations: [
    Sentry.browserTracingIntegration({ router }),
    Sentry.replayIntegration()
  ],
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 1.0,
  replaysOnErrorSampleRate: 1.0
});

const pinia = createPinia();
pinia.use(piniaPluginPersistedState);

app.use(router).use(VueVirtualScroller).use(pinia).mount("#app");

export const refrescarAppCheckToken = async () => {
  await getToken(appCheck, true);
};

auth.onAuthStateChanged(async change => {
  if (change == null) {
    const routeName = router.currentRoute.value.name;

    if (
      routeName != "login" &&
      routeName != "logout" &&
      routeName != "registro" &&
      routeName != "aceptarInvitacion" &&
      routeName != "tokenDepuracion" &&
      routeName != "reservasOceano" &&
      routeName != "reservasSelva"
    ) {
      if (esProduccion) {
        redirigirAccesoAtiendo();
      } else {
        router.push("/login");
      }
    }
  } else {
    await getToken(appCheck, true);
  }
});

onMessage(messaging, payload => {
  navigator.serviceWorker.getRegistrations().then(registrations => {
    const registration = registrations.find(
      reg =>
        reg.active?.state === "activated" &&
        reg.active.scriptURL.endsWith("firebase-messaging-sw.js")
    );
    if (registration && payload && payload.notification && payload.data) {
      registration.showNotification(payload.notification.title!, {
        body: payload.notification.body,
        data: { url: payload.data.url, empresaId: payload.data.empresaId }
      });
    }
  });
});
